import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Plus, Search, Filter, ListMusic, Play, Edit, Trash2, Tag, Clock, RotateCcw } from 'lucide-react';
import { getMobileStyles, ysr2Mobile } from '../styles/mobileDesignSystem';
import MobileCard from './MobileCard';
import { capitalizeWords } from '@/lib/utils';
import { useCurrency } from '@/contexts/CurrencyContext';
import { getMinimumPrice, formatMinimumPrice, getCurrencySymbol, getTestSongMinimumPrice } from '@/lib/currencyConfig';
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';

const MobileCatalogue = ({ 
  songs, 
  archivedSongs = [],
  playlists, 
  activePlaylist,
  catalogueTopSlot = null,
  catalogueToolsSlot = null,
  onAddSong, 
  onEditSong, 
  onDeleteSong,
  onRestoreSong,
  onAddPlaylist,
  onEditPlaylist,
  onSelectPlaylist,
  onMarkAsPlayed
}) => {
  const { currency: profileCurrency, format } = useCurrency();
  const currency = profileCurrency || DEFAULT_CURRENCY;
  const minRegular = getMinimumPrice(currency);
  const minRegularLabel = formatMinimumPrice(currency);
  const testSongMin = getTestSongMinimumPrice(currency);
  const testMinLabel = formatCurrency(testSongMin, currency);
  const ccySym = getCurrencySymbol(currency);

  const [activeTab, setActiveTab] = useState('songs');
  
  // Check if we should start on setlists tab based on URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    console.log('MobileCatalogue: URL tab param:', tabParam);
    if (tabParam === 'setlists') {
      console.log('MobileCatalogue: Setting activeTab to playlists');
      setActiveTab('playlists');
    }
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddSong, setShowAddSong] = useState(false);
  const [songFormData, setSongFormData] = useState({
    title: '',
    original_artist: '',
    price: ''
  });
  const [addedSongs, setAddedSongs] = useState([]);
  const [priceError, setPriceError] = useState(''); // Track price validation error

  const filteredSongs = songs?.filter(song => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const title = (song.title || '').toLowerCase();
    const artist = (song.artist || '').toLowerCase();
    const originalArtist = (song.original_artist || '').toLowerCase();
    return title.includes(query) || artist.includes(query) || originalArtist.includes(query);
  }) || [];

  // Add Song handlers
  const validateSongDraft = (song) => {
    const price = parseFloat(song.price);
    const isTestSong = song.title.trim().toLowerCase() === 'test' && 
                      song.original_artist.trim().toLowerCase() === 'test';
    // Workaround: Allow £0.00 if both title and artist are "free" (case-insensitive)
    const isFreeSong = song.title.trim().toLowerCase() === 'free' && 
                      song.original_artist.trim().toLowerCase() === 'free';
    
    // Validate price before adding
    if (isNaN(price) || price < 0) {
      setPriceError('Price cannot be negative.');
      return null;
    } else if (isFreeSong && price >= 0) {
      // Free song - allow £0.00, no validation needed
    } else if (!isTestSong && !isFreeSong && (isNaN(price) || price < minRegular)) {
      setPriceError(`Minimum price is ${minRegularLabel}. We recommend pricing close to your region's minimum for better earnings.`);
      return null;
    } else if (isTestSong && (isNaN(price) || price < testSongMin)) {
      setPriceError(`Minimum price for test songs is ${testMinLabel}.`);
      return null;
    }

    // Clear error if validation passes
    setPriceError('');

    return {
      ...song,
      id: Date.now().toString(),
      price: price
    };
  };

  const hasDraftSong = () => (
    songFormData.title.trim() &&
    songFormData.original_artist.trim() &&
    songFormData.price.toString().trim()
  );

  const handleAddSong = () => {
    if (!hasDraftSong()) {
      return;
    }

    const newSong = validateSongDraft(songFormData);

    if (!newSong) {
      return;
    }

    setAddedSongs(prev => [...prev, newSong]);
    setSongFormData({ title: '', original_artist: '', price: '' });
  };

  const removeAddedSong = (index) => {
    setAddedSongs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveAndClose = () => {
    const songsToSave = [...addedSongs];
    if (hasDraftSong()) {
      const draftSong = validateSongDraft(songFormData);
      if (!draftSong) {
        return;
      }
      songsToSave.push(draftSong);
    }

    // Validate all songs before saving
    const invalidSongs = [];
    songsToSave.forEach((song, index) => {
      const price = parseFloat(song.price) || 0;
      const isTestSong = song.title.trim().toLowerCase() === 'test' && 
                        song.original_artist.trim().toLowerCase() === 'test';
      // Workaround: Allow £0.00 if both title and artist are "free" (case-insensitive)
      const isFreeSong = song.title.trim().toLowerCase() === 'free' && 
                        song.original_artist.trim().toLowerCase() === 'free';
      
      if (isNaN(price) || price < 0) {
        invalidSongs.push({ index: index + 1, title: song.title, artist: song.original_artist, price, error: 'Price cannot be negative' });
      } else if (isFreeSong && price >= 0) {
        // Free song - allow £0.00, no validation needed
      } else if (!isTestSong && !isFreeSong && (isNaN(price) || price < minRegular)) {
        invalidSongs.push({ index: index + 1, title: song.title, artist: song.original_artist, price });
      } else if (isTestSong && (isNaN(price) || price < testSongMin)) {
        invalidSongs.push({ index: index + 1, title: song.title, artist: song.original_artist, price, isTest: true });
      }
    });

    if (invalidSongs.length > 0) {
      const songDetails = invalidSongs.map(s => 
        `Song ${s.index}: "${s.title}" by ${s.artist} - Currently ${formatCurrency(s.price, currency)}, needs to be at least ${s.isTest ? testMinLabel : minRegularLabel}`
      ).join('\n');
      
      // Show error - we'll need to import useToast or pass it as prop
      alert(`⚠️ Price Too Low - Cannot Save\n\nSome songs are priced below the minimum requirement of ${minRegularLabel} per song.\n\n${songDetails}\n\nPlease update all song prices to at least ${minRegularLabel} to continue.`);
      return;
    }

    // Call the parent's onAddSong function with all added songs
    if (songsToSave.length > 0) {
      onAddSong(songsToSave);
    }
    setShowAddSong(false);
    setSongFormData({ title: '', original_artist: '', price: '' });
    setAddedSongs([]);
    setPriceError('');
  };

  const tabs = [
    { id: 'songs', label: 'Songs', icon: Music, count: songs?.length || 0 },
    { id: 'playlists', label: 'Setlists', icon: ListMusic, count: playlists?.length || 0 },
  ];

  return (
    <div className={`${ysr2Mobile.shell} min-h-[100dvh] space-y-6 px-4 pt-4 pb-44`}>
      <div className={ysr2Mobile.orbBlue} />
      <div className={ysr2Mobile.orbPurple} />
      {/* Header */}
      <motion.div
        className={`${ysr2Mobile.surfaceStrong} relative z-10 p-4 text-center mb-6`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-center space-x-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-500/15 border border-blue-300/30 flex items-center justify-center shadow-[0_0_28px_rgba(59,130,246,0.18)]">
            <Music className="w-6 h-6 text-blue-100" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Catalogue</h1>
            <p className="text-blue-100/70 text-sm">Manage your Songs and Setlists</p>
          </div>
        </div>
      </motion.div>

      {/* Search Bar */}
      <MobileCard className="p-4">
        <div className="flex items-center space-x-3">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search songs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none"
          />
          <Filter className="w-5 h-5 text-gray-400" />
        </div>
      </MobileCard>

      {/* Tab Navigation */}
      <div className="flex space-x-2">
        {tabs.map((tab, index) => (
          <motion.button
            key={tab.id}
            className={`flex-1 flex items-center justify-center space-x-2 p-3 rounded-xl border-2 transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-blue-500/15 border-blue-300/35 text-blue-100 shadow-[0_0_24px_rgba(59,130,246,0.16)]'
                : 'bg-white/[0.045] border-white/10 text-slate-400'
            }`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <tab.icon className="w-4 h-4" />
            <span className="text-sm font-medium">{tab.label}</span>
            <span className="text-xs bg-gray-700/50 px-2 py-1 rounded-full">
              {tab.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Active Playlist Indicator */}
      {activePlaylist && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-green-900/20 border-2 border-green-500/60 rounded-xl p-3"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Play className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-medium">Active Playlist:</span>
              <span className="text-white">{activePlaylist.name}</span>
            </div>
            <span className="text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded-full">
              {activePlaylist.songs?.length || 0} songs
            </span>
          </div>
        </motion.div>
      )}

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'songs' ? (
          <motion.div
            key="songs"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {catalogueTopSlot}
            {catalogueToolsSlot ? (
              <div className="flex justify-end -mt-1">{catalogueToolsSlot}</div>
            ) : null}
            {/* Add Song Button */}
            <motion.button
              className={`w-full ${getMobileStyles.button('primary', 'lg')}`}
              onClick={() => setShowAddSong(true)}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex items-center justify-center space-x-2">
                <Plus className="w-5 h-5" />
                <span>Add New Song</span>
              </div>
            </motion.button>

            {/* Songs List */}
            <div className="space-y-3">
              {filteredSongs.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`${ysr2Mobile.amberCard} p-6 text-center mb-16 overflow-visible`}
                >
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <Music className="w-6 h-6 text-yellow-400" />
                    <h3 className="text-lg font-semibold text-amber-200">Start Building Your Catalogue</h3>
                  </div>
                  <p className="text-amber-100/80 text-sm mb-4">
                    We recommend adding at least 5 songs to get started. This gives your audience more options to choose from!
                  </p>
                  <motion.button
                    className={`${getMobileStyles.button('primary', 'md')}`}
                    onClick={() => setShowAddSong(true)}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Plus className="w-4 h-4" />
                      <span>Add Your First Song</span>
                    </div>
                  </motion.button>
                </motion.div>
              )}
              
              {filteredSongs.map((song, index) => (
                <motion.div
                  key={song.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <MobileCard className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 min-w-0">
                          <Music className="w-4 h-4 text-purple-400 shrink-0" />
                          <h3 className="font-medium text-white truncate min-w-0 flex-1">
                            {capitalizeWords(song.title)}
                          </h3>
                          {song.tags?.length > 0 && (
                            <Tag className="w-3 h-3 text-gray-400 shrink-0" />
                          )}
                          <span className="text-[11px] font-semibold text-purple-300 tabular-nums shrink-0 whitespace-nowrap leading-none">
                            {format(parseFloat(song.price) || 0)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">by {capitalizeWords(song.artist || song.original_artist || 'Unknown Artist')}</p>
                        {song.played_count > 0 && (
                          <div className="flex items-center space-x-1 mt-1">
                            <Play className="w-3 h-3 text-green-400" />
                            <span className="text-xs text-green-400">
                              Played {song.played_count} times
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <motion.button
                          className="w-8 h-8 bg-blue-900/50 border border-blue-600 rounded-full flex items-center justify-center"
                          onClick={() => onEditSong(song)}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Edit className="w-4 h-4 text-blue-400" />
                        </motion.button>
                        
                        <motion.button
                          className="w-8 h-8 bg-red-900/50 border border-red-600 rounded-full flex items-center justify-center"
                          onClick={() => onDeleteSong(song.id)}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </motion.button>
                      </div>
                    </div>
                  </MobileCard>
                </motion.div>
              ))}
            </div>

            {/* Archived Songs Section */}
            {archivedSongs && archivedSongs.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="mt-8"
              >
                <div className="flex items-center space-x-2 mb-4">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-400">Archived Songs ({archivedSongs.length})</h3>
                </div>
                
                <div className="space-y-3">
                  {archivedSongs.map((song, index) => (
                    <motion.div
                      key={song.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <MobileCard className="p-4 bg-gray-800/30 border-gray-600/40">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 min-w-0">
                              <Music className="w-4 h-4 text-gray-400 shrink-0" />
                              <h3 className="font-medium text-gray-400 truncate min-w-0 flex-1">
                                {capitalizeWords(song.title)}
                              </h3>
                              {song.tags?.length > 0 && (
                                <Tag className="w-3 h-3 text-gray-500 shrink-0" />
                              )}
                              <span className="text-[11px] font-semibold text-purple-400/90 tabular-nums shrink-0 whitespace-nowrap leading-none">
                                {format(parseFloat(song.price) || 0)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                            {song.played_count > 0 && (
                              <div className="flex items-center space-x-1 mt-1">
                                <Play className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-500">
                                  Played {song.played_count} times
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <motion.button
                              className="w-8 h-8 bg-green-900/50 border border-green-600 rounded-full flex items-center justify-center"
                              onClick={() => onRestoreSong && onRestoreSong(song)}
                              whileTap={{ scale: 0.9 }}
                            >
                              <RotateCcw className="w-4 h-4 text-green-400" />
                            </motion.button>
                            
                            <motion.button
                              className="w-8 h-8 bg-red-900/50 border border-red-600 rounded-full flex items-center justify-center"
                              onClick={() => onDeleteSong(song.id)}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </motion.button>
                          </div>
                        </div>
                      </MobileCard>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="playlists"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Add Playlist Button */}
            <motion.button
              className={`w-full ${getMobileStyles.button('success', 'lg')}`}
              onClick={() => {
                console.log('🔍 MobileCatalogue: Create Setlist button clicked');
                if (onAddPlaylist) {
                  console.log('🔍 MobileCatalogue: Calling onAddPlaylist');
                  onAddPlaylist();
                } else {
                  console.log('🚫 MobileCatalogue: onAddPlaylist is not defined');
                }
              }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex items-center justify-center space-x-2">
                <Plus className="w-5 h-5" />
                <span>Create Setlist</span>
              </div>
            </motion.button>


            {/* Playlists List */}
            <div className="space-y-3">
              {playlists && playlists.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-yellow-900/20 border-2 border-yellow-500/60 rounded-xl p-6 text-center"
                >
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <ListMusic className="w-6 h-6 text-yellow-400" />
                    <h3 className="text-lg font-semibold text-yellow-400">No Setlists Created</h3>
                  </div>
                  <p className="text-yellow-300 text-sm mb-4">
                    Create setlists to organize your songs for different performances
                  </p>
                  <motion.button
                    className={`${getMobileStyles.button('primary', 'md')}`}
                    onClick={() => {
                      console.log('🔍 MobileCatalogue: Create Your First Setlist button clicked');
                      if (onAddPlaylist) {
                        console.log('🔍 MobileCatalogue: Calling onAddPlaylist from empty state');
                        onAddPlaylist();
                      } else {
                        console.log('🚫 MobileCatalogue: onAddPlaylist is not defined');
                      }
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Plus className="w-4 h-4" />
                      <span>Create Your First Setlist</span>
                    </div>
                  </motion.button>
                </motion.div>
              )}
              
              {playlists?.map((playlist, index) => (
                <motion.div
                  key={playlist.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="w-full"
                >
                  <MobileCard 
                    variant={activePlaylist?.id === playlist.id ? 'success' : 'default'}
                    className="w-full !p-4"
                    onClick={() => onSelectPlaylist(playlist)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <ListMusic className="w-4 h-4 text-green-400" />
                          <h3 className="font-medium text-white">{playlist.name}</h3>
                        </div>
                        <p className="text-sm text-gray-400">
                          {playlist.songs?.length || 0} songs
                        </p>
                        {playlist.description && (
                          <p className="text-xs text-gray-500 mt-1">{playlist.description}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {activePlaylist?.id === playlist.id && (
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        )}
                        <motion.button
                          className="w-8 h-8 bg-blue-900/50 border border-blue-600 rounded-full flex items-center justify-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onEditPlaylist) {
                              onEditPlaylist(playlist);
                            } else {
                              console.log('🚫 onEditPlaylist not defined');
                            }
                          }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Edit className="w-4 h-4 text-blue-400" />
                        </motion.button>
                      </div>
                    </div>
                  </MobileCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* Add Song Modal */}
      <AnimatePresence>
        {showAddSong && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center px-3 z-[99999]"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md overflow-hidden"
              style={{
                maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px)',
              }}
            >
              <MobileCard variant="ysr2Strong" className="flex max-h-[inherit] flex-col overflow-hidden !p-0">
                <div className="flex flex-shrink-0 items-center justify-between border-b border-blue-300/10 p-5">
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 rounded-2xl bg-blue-500/15 border border-blue-300/25 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-blue-100" />
                    </div>
                    <h2 className="text-xl font-semibold text-white">Add New Song</h2>
                  </div>
                  <motion.button
                    onClick={() => setShowAddSong(false)}
                    className="w-9 h-9 bg-white/10 border border-white/10 rounded-full flex items-center justify-center"
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-gray-400 text-lg">×</span>
                  </motion.button>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleAddSong();
                }} className="flex min-h-0 flex-1 flex-col">
                  <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Song Title *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter song title"
                        value={songFormData.title}
                        onChange={(e) => setSongFormData(prev => ({ ...prev, title: e.target.value }))}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        className={`${ysr2Mobile.input} w-full`}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Original Artist *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter original artist"
                        value={songFormData.original_artist}
                        onChange={(e) => setSongFormData(prev => ({ ...prev, original_artist: e.target.value }))}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        className={`${ysr2Mobile.input} w-full`}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Price ({ccySym}) *
                        <span className="text-xs text-gray-400 ml-2">Minimum: {minRegularLabel}</span>
                      </label>
                      <input
                        type="number"
                        placeholder="3.00"
                        min={minRegular}
                        step="0.01"
                        value={songFormData.price}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSongFormData(prev => ({ ...prev, price: value }));
                          
                          // Validate price and set error
                          const numValue = parseFloat(value);
                          const isTestSong = songFormData.title.trim().toLowerCase() === 'test' && 
                                            songFormData.original_artist.trim().toLowerCase() === 'test';
                          
                          if (value === '' || isNaN(numValue)) {
                            setPriceError('');
                          } else if (!isTestSong && numValue < minRegular) {
                            setPriceError(`Minimum price is ${minRegularLabel}. We recommend pricing close to your region's minimum for better earnings.`);
                          } else if (isTestSong && numValue < testSongMin) {
                            setPriceError(`Minimum price for test songs is ${testMinLabel}.`);
                          } else {
                            setPriceError('');
                          }
                        }}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        className={`${ysr2Mobile.input} w-full ${
                          priceError ? '!border-red-500 focus:!border-red-500' : ''
                        }`}
                        required
                      />
                      {priceError && (
                        <p className="text-sm text-red-400 mt-1 flex items-start">
                          <span className="mr-1">⚠️</span>
                          <span>{priceError}</span>
                        </p>
                      )}
                      {!priceError && songFormData.price && parseFloat(songFormData.price) >= minRegular && (
                        <p className="text-xs text-green-400 mt-1">✓ Price meets minimum requirement</p>
                      )}
                    </div>

                    {/* Added Songs List */}
                    {addedSongs.length > 0 && (
                      <div className="bg-gray-800/30 border border-gray-600/40 rounded-xl p-4">
                        <h4 className="text-sm font-medium text-gray-300 mb-3">Added Songs ({addedSongs.length})</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {addedSongs.map((song, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-700/30 rounded-lg p-2">
                              <div className="flex-1">
                                <div className="text-sm text-white font-medium">{capitalizeWords(song.title)}</div>
                                <div className="text-xs text-gray-400">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</div>
                              </div>
                              <motion.button
                                onClick={() => removeAddedSong(index)}
                                className="w-6 h-6 bg-red-900/50 border border-red-600 rounded-full flex items-center justify-center"
                                whileTap={{ scale: 0.9 }}
                              >
                                <span className="text-red-400 text-xs">×</span>
                              </motion.button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid flex-shrink-0 grid-cols-2 gap-3 border-t border-blue-300/10 bg-slate-950/90 px-5 py-4">
                    <motion.button
                      type="button"
                      className={`${ysr2Mobile.secondaryButton} px-3 py-3 text-sm font-semibold disabled:opacity-50`}
                      onClick={handleSaveAndClose}
                      disabled={addedSongs.length === 0 && !hasDraftSong()}
                      whileTap={{ scale: 0.95 }}
                    >
                      Save & Close
                    </motion.button>
                    
                    <motion.button
                      type="submit"
                      className={`${ysr2Mobile.primaryButton} px-3 py-3 text-sm font-semibold`}
                      whileTap={{ scale: 0.95 }}
                    >
                      Add Another Song
                    </motion.button>
                  </div>
                </form>
              </MobileCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileCatalogue;
